import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Mail } from "lucide-react";

const NCC_LOGO = "/ncc-logo.png";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#f5f7fc]" data-testid="privacy-policy-page">
      {/* Branded header */}
      <header className="border-b-2 border-ncc-gold bg-ncc-navy print:hidden">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3 md:px-8">
          <Link to="/" className="flex h-12 items-center rounded-md bg-white px-2 py-1" data-testid="privacy-nav-logo">
            <img src={NCC_LOGO} alt="NCC-SWMS · MANPOWER DATABANK" className="h-full w-auto object-contain" />
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="inline-flex items-center gap-2 rounded-md bg-ncc-gold px-3 py-2 text-xs font-bold uppercase tracking-wider text-ncc-navy transition-all hover:-translate-y-0.5 hover:bg-ncc-gold2"
          >
            <ArrowLeft className="h-4 w-4" /> Back to App
          </Link>
        </div>
        <div className="h-1 bg-gradient-to-r from-ncc-navy via-ncc-gold to-ncc-gold2" />
      </header>

      <main className="mx-auto max-w-4xl px-4 py-10 md:px-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-10">
          <div className="flex items-start gap-4">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-[#eef2ff] text-[#1e3a8a]">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#1e3a8a]">// Privacy Policy</div>
              <h1 className="mt-1 font-heading text-3xl font-black uppercase tracking-tight text-slate-900 md:text-4xl">
                NCC-SWMS Manpower Databank
              </h1>
              <div className="mt-3 grid gap-1 text-sm text-slate-600">
                <p><span className="font-semibold text-slate-800">Effective date:</span> 14 February 2026</p>
                <p><span className="font-semibold text-slate-800">Operator:</span> NCC Limited (Internal Tool)</p>
                <p className="flex items-center gap-1.5">
                  <span className="font-semibold text-slate-800">Contact:</span>
                  <a href="mailto:shamshuddin.k@nccltd.in" className="inline-flex items-center gap-1 text-[#1e3a8a] hover:underline">
                    <Mail className="h-3.5 w-3.5" /> shamshuddin.k@nccltd.in
                  </a>
                </p>
              </div>
            </div>
          </div>

          <p className="mt-8 text-[15px] leading-relaxed text-slate-700">
            NCC-SWMS Manpower Databank (&ldquo;the App&rdquo;) is an internal application used by NCC Limited
            to maintain a directory of approved labour contractors and on-roll payroll manpower.
            This policy explains what data we collect, why, and how it is protected.
          </p>

          <Section n="1" title="Information we collect">
            <p>
              The App stores only the information that contractors and payroll employees voluntarily
              provide during self-registration, plus what NCC administrators record on their behalf:
            </p>
            <ul className="mt-3 space-y-1.5">
              <Bullet>Full name, mobile number, optional email address</Bullet>
              <Bullet>City and state of work base</Bullet>
              <Bullet>Trade(s) offered and crew size / years of experience</Bullet>
              <Bullet>PAN / Aadhar number used for KYC verification</Bullet>
              <Bullet>NCC site name (for on-roll employees / NCCL contractors)</Bullet>
              <Bullet>Profile photo, ID / license documents, certifications</Bullet>
              <Bullet>Self-reported availability signals (&ldquo;Ready to Mobilize&rdquo; / &ldquo;Ready to Join&rdquo;)</Bullet>
            </ul>
            <p className="mt-3">
              We <span className="font-bold">do not</span> collect device identifiers, precise GPS location,
              advertising IDs, contacts, SMS, microphone, camera (beyond user-initiated file uploads),
              or any data unrelated to manpower vetting.
            </p>
          </Section>

          <Section n="2" title="How information is used">
            <ul className="space-y-1.5">
              <Bullet>To match approved contractors and payroll manpower with NCC project sites.</Bullet>
              <Bullet>To allow NCC administrators to verify identity, trades, and availability.</Bullet>
              <Bullet>To send approval / rejection / re-approval notifications via email (Resend) and optional push notifications (Web Push).</Bullet>
            </ul>
          </Section>

          <Section n="3" title="Sharing">
            <p>
              We <span className="font-bold">do not</span> sell, rent, or share any personal data with
              third parties for marketing. Data is accessible only to:
            </p>
            <ul className="mt-3 space-y-1.5">
              <Bullet>The contractor / payroll employee themselves (read-only via mobile login)</Bullet>
              <Bullet>Authorised NCC Limited administrators (role-based access)</Bullet>
              <Bullet>Sub-processors needed strictly to operate the service: MongoDB Atlas (storage), Resend (transactional email), and the hosting provider.</Bullet>
            </ul>
          </Section>

          <Section n="4" title="Data retention">
            <ul className="space-y-1.5">
              <Bullet>Records are retained while the contractor / employee is active in the NCC vendor pool.</Bullet>
              <Bullet>Self-withdrawn or rejected records are soft-deleted (<code className="rounded bg-slate-100 px-1 font-mono text-[12px] text-slate-800">status=rejected, withdrawn=true</code>) and permanently purged by an administrator within 90 days unless a legal hold applies.</Bullet>
            </ul>
          </Section>

          <Section n="5" title="Security">
            <ul className="space-y-1.5">
              <Bullet>All traffic is served over HTTPS (TLS 1.2+).</Bullet>
              <Bullet>Admin authentication uses bcrypt-hashed passwords plus JWT sessions.</Bullet>
              <Bullet>Contractor / payroll login uses mobile-number identity (no public password); access is limited to the user&apos;s own profile.</Bullet>
            </ul>
          </Section>

          <Section n="6" title="User rights">
            <p>
              You may request access, correction, export, or deletion of your data at any time by emailing{" "}
              <a href="mailto:shamshuddin.k@nccltd.in" className="font-bold text-[#1e3a8a] hover:underline">
                shamshuddin.k@nccltd.in
              </a>
              . Approved users can also withdraw their registration from inside the app.
            </p>
          </Section>

          <Section n="7" title="Children">
            <p>The App is <span className="font-bold">not</span> intended for users under 18.</p>
          </Section>

          <Section n="8" title="Changes to this policy">
            <p>
              Material changes will be communicated by email to registered admins and by an in-app notice.
              The &ldquo;Effective date&rdquo; above will be updated.
            </p>
          </Section>

          <Section n="9" title="Grievance officer">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
              <p><span className="font-semibold text-slate-800">Name:</span> Shamshuddin K</p>
              <p className="mt-1">
                <span className="font-semibold text-slate-800">Email:</span>{" "}
                <a href="mailto:shamshuddin.k@nccltd.in" className="text-[#1e3a8a] hover:underline">
                  shamshuddin.k@nccltd.in
                </a>
              </p>
              <p className="mt-1"><span className="font-semibold text-slate-800">Address:</span> NCC Limited, Hyderabad, Telangana, India</p>
            </div>
          </Section>

          <div className="mt-10 border-t border-slate-200 pt-4 text-center text-xs text-slate-500">
            © {new Date().getFullYear()} NCC Limited. All rights reserved.
          </div>
        </div>
      </main>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section className="mt-8" data-testid={`privacy-section-${n}`}>
      <h2 className="flex items-baseline gap-3 font-heading text-lg font-extrabold uppercase tracking-wide text-[#1e3a8a]">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-ncc-gold text-sm font-black text-ncc-navy">
          {n}
        </span>
        {title}
      </h2>
      <div className="mt-3 space-y-2 text-[15px] leading-relaxed text-slate-700">{children}</div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-ncc-gold" />
      <span>{children}</span>
    </li>
  );
}
